import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  Group,
  Loader,
  Modal,
  PageHeader,
  Select,
  Stack,
  Text,
  TextInput,
} from '@vipso/shared';
import {
  useAssignOrgFeatureSetMutation,
  useGetFeaturesQuery,
  useGetOrgsQuery,
  useUpdateFeatureSetFeaturesMutation,
  type FeatureSetFeatureRow,
} from '../api/adminApi';

export function FeaturesPage() {
  const { data, isLoading, isError, refetch } = useGetFeaturesQuery();
  const { data: orgsData } = useGetOrgsQuery({ status: 'Active', page: 1, pageLimit: 100 });
  const [assignOpen, setAssignOpen] = useState(false);
  const [orgId, setOrgId] = useState('');
  const [featureSetId, setFeatureSetId] = useState('');
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [assignFeatureSet, assignState] = useAssignOrgFeatureSetMutation();
  const [updateFeatureSetFeatures, saveState] = useUpdateFeatureSetFeaturesMutation();
  const [assignMsg, setAssignMsg] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [featureDraft, setFeatureDraft] = useState<
    Record<string, { displayName: string; isEnabled: boolean }>
  >({});

  const features = data?.features ?? [];
  const sets = data?.featureSets ?? [];

  const selectedSet = useMemo(
    () => sets.find((s) => s.id === selectedSetId) ?? null,
    [sets, selectedSetId],
  );

  const setRows = selectedSet?.features ?? [];

  useEffect(() => {
    const rows = selectedSet?.features ?? [];
    const map: Record<string, { displayName: string; isEnabled: boolean }> = {};
    for (const row of rows) {
      map[row.id] = {
        displayName: row.displayName ?? '',
        isEnabled: row.isEnabled,
      };
    }
    setFeatureDraft(map);
  }, [selectedSet]);

  const orgOptions = useMemo(
    () =>
      (orgsData?.orgs.rows ?? []).map((o) => ({
        value: o.id,
        label: o.name,
      })),
    [orgsData?.orgs],
  );

  const setOptions = useMemo(
    () =>
      sets.map((s) => ({
        value: s.id,
        label: `${s.name}${s.isSystemDefault ? ' (default)' : ''}`,
      })),
    [sets],
  );

  async function handleAssign() {
    setAssignMsg(null);
    setAssignError(null);
    try {
      await assignFeatureSet({ orgId, featureSetId }).unwrap();
      // Domain emits a single quiet force-refresh for featureKeys — no admin duplicate.
      setAssignMsg('Feature set assigned.');
      setOrgId('');
      setFeatureSetId('');
      setAssignOpen(false);
      await refetch();
    } catch {
      setAssignError('Failed to assign feature set.');
    }
  }

  async function handleSaveSet() {
    if (!selectedSetId) return;
    setSaveMsg(null);
    setSaveError(null);
    try {
      const items = setRows.map((row) => {
        const draft = featureDraft[row.id] ?? {
          displayName: row.displayName ?? '',
          isEnabled: row.isEnabled,
        };
        return {
          id: row.id,
          displayName: draft.displayName.trim() || null,
          isEnabled: draft.isEnabled,
        };
      });
      await updateFeatureSetFeatures({ featureSetId: selectedSetId, items }).unwrap();
      setSaveMsg('Feature set saved.');
      await refetch();
    } catch {
      setSaveError('Failed to save feature set.');
    }
  }

  if (isLoading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Feature catalog"
        description="Global feature keys and feature sets for org assignment. Edit display names on a set to rename UI entities per org."
        actions={
          <Button onClick={() => setAssignOpen(true)}>Assign feature set</Button>
        }
      />
      {isError ? <Alert color="red" title="Failed to load features" /> : null}
      {assignMsg ? <Alert color="green" title={assignMsg} /> : null}
      {saveMsg ? <Alert color="green" title={saveMsg} /> : null}
      {saveError ? <Alert color="red" title={saveError} /> : null}

      <Text fw={600}>Feature sets ({sets.length})</Text>
      <DataTable
        columns={[
          { key: 'id', header: 'ID', render: (r: { id: string }) => r.id },
          { key: 'name', header: 'Name', render: (r: { name: string }) => r.name },
          { key: 'org', header: 'Org ID', render: (r: { orgId: string }) => r.orgId },
          {
            key: 'default',
            header: 'Default',
            render: (r: { isSystemDefault?: boolean }) => (r.isSystemDefault ? 'yes' : '—'),
          },
          {
            key: 'edit',
            header: '',
            render: (r: { id: string }) => (
              <Button
                size="xs"
                variant={selectedSetId === r.id ? 'filled' : 'light'}
                onClick={() => setSelectedSetId(r.id)}
              >
                Edit features
              </Button>
            ),
          },
        ]}
        rows={sets}
        getRowId={(r) => r.id}
        testId="feature-sets-table"
      />

      {selectedSet ? (
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={600}>
              Features in set: {selectedSet.name} (org {selectedSet.orgId})
            </Text>
            <Group gap="xs">
              <Button variant="default" onClick={() => setSelectedSetId(null)}>
                Close
              </Button>
              <Button loading={saveState.isLoading} onClick={() => void handleSaveSet()}>
                Save
              </Button>
            </Group>
          </Group>
          <Text size="sm" c="dimmed">
            Set display name on the module&apos;s primary row (e.g. customer.view → Farmers) to
            rename that entity in the tenant app.
          </Text>
          <DataTable
            columns={[
              {
                key: 'key',
                header: 'Feature key',
                render: (r: FeatureSetFeatureRow) => r.featureKey,
              },
              {
                key: 'catalog',
                header: 'Catalog name',
                render: (r: FeatureSetFeatureRow) => r.name || '—',
              },
              {
                key: 'displayName',
                header: 'Display name override',
                render: (r: FeatureSetFeatureRow) => (
                  <TextInput
                    size="xs"
                    placeholder="e.g. Farmers"
                    value={featureDraft[r.id]?.displayName ?? r.displayName ?? ''}
                    onChange={(e) => {
                      const displayName = e.currentTarget.value;
                      setFeatureDraft((prev) => ({
                        ...prev,
                        [r.id]: {
                          displayName,
                          isEnabled: prev[r.id]?.isEnabled ?? r.isEnabled,
                        },
                      }));
                    }}
                  />
                ),
              },
              {
                key: 'enabled',
                header: 'Enabled',
                render: (r: FeatureSetFeatureRow) => (
                  <Checkbox
                    checked={featureDraft[r.id]?.isEnabled ?? r.isEnabled}
                    onChange={(e) => {
                      const isEnabled = e.currentTarget.checked;
                      setFeatureDraft((prev) => ({
                        ...prev,
                        [r.id]: {
                          displayName: prev[r.id]?.displayName ?? r.displayName ?? '',
                          isEnabled,
                        },
                      }));
                    }}
                  />
                ),
              },
            ]}
            rows={setRows}
            getRowId={(r) => r.id}
            testId="feature-set-features-table"
          />
        </Stack>
      ) : null}

      <Text fw={600}>Features ({features.length})</Text>
      <DataTable
        columns={[
          { key: 'id', header: 'ID', render: (r: { id: string }) => r.id },
          { key: 'key', header: 'Key', render: (r: { featureKey: string }) => r.featureKey },
          { key: 'name', header: 'Name', render: (r: { name?: string }) => r.name || '—' },
          { key: 'module', header: 'Module', render: (r: { module?: string }) => r.module || '—' },
        ]}
        rows={features}
        getRowId={(r) => r.id}
        testId="features-table"
      />

      <Modal
        opened={assignOpen}
        onClose={() => setAssignOpen(false)}
        title="Assign feature set to organization"
      >
        <Stack gap="md">
          {assignError ? <Alert color="red" title={assignError} /> : null}
          <Select
            label="Organization"
            placeholder="Select active org"
            value={orgId}
            onChange={(v) => setOrgId(v || '')}
            data={orgOptions}
            required
          />
          <Select
            label="Feature set"
            placeholder="Select feature set"
            value={featureSetId}
            onChange={(v) => setFeatureSetId(v || '')}
            data={setOptions}
            required
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAssignOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={assignState.isLoading}
              disabled={!orgId || !featureSetId}
              onClick={() => void handleAssign()}
            >
              Assign
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
